<template>
  <b-sidebar
    :id="drawer.id"
    :title="drawer.title"
    backdrop-variant="dark"
    backdrop
    right
    shadow
    width="620px"
    :visible="drawer.visible"
    sidebar-class="right-drawer"
    aria-hidden="false"
    @hidden="onClose"
  >
    <div class="flex flex-col p-2">
      <div class="flex items-start p-[.75rem] gap-[.75rem] rounded-lg bg-[#DFF3FF] border-blue mb-2">
        <img
          src="https://storage.googleapis.com/komerce/assets/icons/info-circle.svg"
          alt="info-icon"
          class="w-[20px]"
        >
        <p class="m-0 leading-5">
          Shifting memiliki prioritas lebih tinggi daripada mode kerja default (WFO/WFH).
        </p>
      </div>
      <div class="border-strip mb-2 pb-1">
        <div v-if="drawer.triggerSource === 'table-action-btn'">
          <p class="text-[14px] text-black font-medium m-0">
            {{ drawer.data.full_name }}
          </p>
          <p class="text-[12px] text-[#828282] m-0">
            {{ drawer.data.position_name }}
          </p>
        </div>
        <div v-else>
          <label
            for="division"
            class="text-base"
          >Divisi</label>
          <v-select
            v-model="filter.division"
            label="division_name"
            :reduce="option => option"
            :options="divisions"
            placeholder="Cari atau pilih divisi"
            @option:selected="getSchedules"
          />
        </div>
      </div>
      <ValidationObserver #default="{ invalid }">
        <div class="p-2 rounded-lg border mb-2 relative">
          <div
            v-show="!(isEnableShiftingDivision || isEnableShiftingUser)"
            class="absolute inset-0 bg-white opacity-40 z-10"
          />
          <p class="text-black text-xl font-medium mb-1">
            Pengaturan Shifting
          </p>
          <p>Atur status kehadiran per hari.</p>
          <div class="border-top py-[1rem] space-y-[1rem]">
            <div
              v-for="({ form, indonesian_day_of_week }, index) in schedules"
              :key="index"
              class="grid grid-cols-2 gap-x-[1rem] items-start"
            >
              <div
                class="flex items-center justify-between gap-x-[1rem]"
              >
                <span>{{ indonesian_day_of_week }}</span>
                <BTabs
                  v-model="form.work_mode"
                  class="rounded-pill bg-[#E2E2E2] p-[.25rem]"
                  pills
                  active-nav-item-class="rounded-pill"
                  nav-class="mb-0"
                >
                  <BTab
                    v-for="(mode, tabIndex) in workModeOptions"
                    :key="tabIndex"
                    :title="mode"
                    :active="tabIndex === form.work_mode"
                    lazy
                    title-item-class="bg-transparent rounded-pill w-1/2"
                  />
                </BTabs>
              </div>
              <div
                class="flex items-stretch justify-between gap-x-[.5rem]"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  :rules="`required|beforeTime:@end_clock_${index}`"
                  name="Kedatangan"
                  :vid="`start_clock_${index}`"
                  class="w-full"
                >
                  <TimePicker
                    v-model="form.start_clock"
                    :icon="errors.length > 0 ? '' : 'k-clock-1 text-[16px]'"
                    :disabled="!(isEnableShiftingDivision || isEnableShiftingUser)"
                    :input-class="{
                      'is-invalid': errors.length > 0,
                    }"
                  />
                  <span
                    v-if="errors.length > 0"
                    class="text-danger text-sm"
                  >{{ errors[0] }}</span>
                </ValidationProvider>
                <span class="mt-[15px]">-</span>
                <ValidationProvider
                  v-slot="{ errors }"
                  :rules="`required|afterTime:@start_clock_${index}`"
                  name="Kepulangan"
                  :vid="`end_clock_${index}`"
                  class="w-full"
                >
                  <TimePicker
                    v-model="form.end_clock"
                    :icon="errors.length > 0 ? '' : 'k-clock-1 text-[16px]'"
                    :disabled="!(isEnableShiftingDivision || isEnableShiftingUser)"
                    :input-class="{
                      'is-invalid': errors.length > 0,
                    }"
                  />
                  <span
                    v-if="errors.length > 0"
                    class="text-danger text-sm"
                  >{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-between gap-[1rem] mt-auto">
          <BButton
            size="md"
            variant="outline-primary"
            block
            @click="onClose"
          >
            Batal
          </BButton>
          <BButton
            size="md"
            :variant="(isEnableShiftingDivision || isEnableShiftingUser) && !invalid ? 'primary' : 'secondary'"
            :disabled="!(isEnableShiftingDivision || isEnableShiftingUser) || invalid || drawer.isLoading"
            block
            class="m-0"
            @click.prevent="handleSaveSchedulesConfig"
          >
            <b-spinner
              v-if="drawer.isLoading"
              small
            />
            Simpan Pengaturan
          </BButton>
        </div>
      </ValidationObserver>
    </div>
  </b-sidebar>
</template>

<style scoped>
.border-blue {
  border: 1px solid #08A0F7;
}

.border-strip {
  border-bottom: none;
  background-image: repeating-linear-gradient(
    to right,
    #E2E2E2 0,
    #E2E2E2 6px,
    transparent 6px,
    transparent 14px
  );
  background-position: bottom;
  background-size: 100% 1.5px;
  background-repeat: no-repeat;
}
</style>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import TimePicker from '@/@core/components/time-picker/TimePicker.vue'
import { komtimAxiosIns } from '@/libs/axios'
import { defaultSchedules, indonesianDayNames, workModes } from './constants'

export default {
  components: {
    vSelect,
    TimePicker,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    drawer: {
      type: Object,
      default: () => ({
        visible: false,
        id: 'shifting-drawer',
        title: 'Judul Drawer',
        triggerSource: '', // ('table-action-btn' | 'shifting-division-btn')
        isLoading: false,
        data: () => ({}),
      }),
    },
    divisions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      workModeOptions: workModes,
      filter: {
        division: null,
      },
      schedules: defaultSchedules,
    }
  },
  computed: {
    /**
     * Check if the drawer is triggered by division shifting
     */
    isEnableShiftingDivision() {
      return this.drawer.triggerSource === 'shifting-division-btn' && this.filter.division
    },
    /**
     * Check if the drawer is triggered by table action button
     */
    isEnableShiftingUser() {
      return this.drawer.triggerSource === 'table-action-btn' && this.drawer.data.user_id
    },
  },
  watch: {
    /**
     * Watch for drawer visibility changes
     */
    'drawer.visible': {
      handler(val) {
        if (val) {
          this.getSchedules()
        }
      },
    },
  },
  methods: {
    /**
     * Get schedules data
     */
    async getSchedules() {
      try {
        const shifting = {
          'shifting-division-btn': this.filter.division,
          'table-action-btn': this.drawer.data?.user_id,
        }
        if (!shifting[this.drawer.triggerSource]) {
          return
        }

        const shiftByDivisionUrl = `/v1/user_work_configurations/division/${this.filter.division?.id}`
        const shiftByUserUrl = `/v1/user_work_configurations/user/${this.drawer.data?.user_id}`
        const url = this.isEnableShiftingUser ? shiftByUserUrl : shiftByDivisionUrl
        const { data: { data } } = await komtimAxiosIns.get(url)

        this.schedules = data.map(item => ({
          ...item,
          indonesian_day_of_week: indonesianDayNames[item.day_of_week - 1],
          form: {
            work_mode: this.workModeOptions.findIndex(mode => mode === item.work_mode),
            start_clock: item.check_in_time,
            end_clock: item.check_out_time,
          },
        }))
      } catch (e) {
        console.warn(e)
      }
    },
    /**
     * Handle submit action
     */
    handleSaveSchedulesConfig() {
      const payload = {
        schedule: this.schedules.map(({ day_of_week, form }) => ({
          day_of_week,
          work_mode: this.workModeOptions[form.work_mode],
          check_in_time: form.start_clock,
          check_out_time: form.end_clock,
        })),
      }

      this.$emit('on-submit', { payload, division: this.filter.division })
    },
    /**
     * Handle close action
     */
    onClose() {
      this.$emit('on-close')
      this.filter.division = null
      this.schedules = defaultSchedules
    },
  },
}
</script>
